/**
 * Admin Subdomain Routes — admin.pawtenant.com.
 *
 * Staff workspace, Company OS and the Staff & Provider sign-in. No public
 * marketing pages are exposed: the catch-all re-hosts a route another portal
 * owns (PAWTENANT-BRANDED-PORTAL-HOSTS-LIVE-001, src/lib/portalHosts.ts) and
 * sends anything unknown to /admin-login.
 *
 * /  → /admin-login
 */
import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { PortalCatchAll } from "../components/feature/PortalHostGuard";

const AdminLoginPage        = lazy(() => import("../pages/admin-login/page"));
const AdminOrdersPage       = lazy(() => import("../pages/admin-orders/page"));
const AdminPartnersRedirect = lazy(() => import("../pages/admin-orders/components/partner-platform/AdminPartnersRedirect"));
const AdminDoctorsPage      = lazy(() => import("../pages/admin-doctors/page"));
const AdminGuidePage        = lazy(() => import("../pages/admin-guide/page"));
const AdminLiveVisitorsPage = lazy(() => import("../pages/admin-live/page"));
const AdminProviderPreview  = lazy(() => import("../pages/admin-orders/components/AdminProviderPreview"));
const CompanyHomePage       = lazy(() => import("../pages/company-home/page"));
const GoLivePage            = lazy(() => import("../pages/go-live/page"));
const ResetPasswordPage = lazy(() => import("../pages/reset-password/page"));
const ProviderLoginPage  = lazy(() => import("../pages/provider-login/page"));
const ProviderPortalPage = lazy(() => import("../pages/provider-portal/page"));
// Admin "view as customer" preview (?preview_email=) and the returning-customer
// checkout hand-off both need the admin's own session, so they run here.
const MyOrdersPage        = lazy(() => import("../pages/my-orders/page"));
const AccountCheckoutPage = lazy(() => import("../pages/account-checkout/page"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f1e1a]">
      <div className="flex flex-col items-center gap-3">
        <i className="ri-loader-4-line animate-spin text-3xl text-[#4ecdc4]"></i>
        <span className="text-sm text-white/40 font-medium">Loading admin portal...</span>
      </div>
    </div>
  );
}

function P({ C }: { C: React.ComponentType }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <C />
    </Suspense>
  );
}

export function AdminSubdomainRoutes() {
  return (
    <Routes>
      {/* Root → admin login */}
      <Route path="/" element={<Navigate to="/admin-login" replace />} />

      {/* Admin portal */}
      <Route path="/admin-login"        element={<P C={AdminLoginPage} />} />
      {/* Visitor Intelligence — Phase 1. More specific than /admin-orders so
          declared first; React Router v6 picks by specificity but listing it
          here also keeps it visually grouped with the orders surface. */}
      <Route path="/admin-orders/live"  element={<P C={AdminLiveVisitorsPage} />} />
      <Route path="/admin-orders"       element={<P C={AdminOrdersPage} />} />
      {/* PARTNER-PLATFORM-ADMIN-WORKSPACE-001 — redirects into ?tab=partners
          on the shell, the same pattern as /admin-chats below. */}
      <Route path="/admin-partners"     element={<P C={AdminPartnersRedirect} />} />
      <Route path="/admin-doctors"      element={<P C={AdminDoctorsPage} />} />
      <Route path="/admin-guide"        element={<P C={AdminGuidePage} />} />
      <Route path="/admin/provider-preview" element={<P C={AdminProviderPreview} />} />

      {/* Company OS — staff sign-in lands here (admin-login safeAdminDest). */}
      <Route path="/company"            element={<P C={CompanyHomePage} />} />
      <Route path="/go-live"            element={<P C={GoLivePage} />} />

      {/* Chats moved inside /admin-orders as a tab. Old /admin-chats path
          redirects there for any stale bookmarks. */}
      <Route path="/admin-chats" element={<Navigate to="/admin-orders" replace />} />

      {/* Shared auth utility */}
      <Route path="/reset-password" element={<P C={ResetPasswordPage} />} />

      {/* Provider portal — a provider who signs in at the Staff & Provider
          Portal continues here with the same session. */}
      <Route path="/provider-login"  element={<P C={ProviderLoginPage} />} />
      <Route path="/provider-portal" element={<P C={ProviderPortalPage} />} />

      {/* Admin-only tools that render customer pages with the admin session. */}
      <Route path="/my-orders"        element={<P C={MyOrdersPage} />} />
      <Route path="/account/checkout" element={<P C={AccountCheckoutPage} />} />

      {/* Catch-all: re-host another portal's route, else the admin login */}
      <Route path="*" element={<PortalCatchAll fallback="/admin-login" />} />
    </Routes>
  );
}
